import type { Task } from "../tasks/types.js";
import type { RecurrenceRule } from "../recurrence/types.js";
import type { RecurrenceOverride } from "../recurrence/overrides/types.js";
import type { Label } from "../labels/types.js";
import type { Topic } from "../topics/types.js";
import type { Calendar } from "../calendars/types.js";
import type { ThemeConfig } from "../theme/types.js";
import type { AppPreferencesRecord } from "../backup/types.js";

/** The minimal shape every persisted row shares (see CLAUDE.md rule 7). `rev` is
 *  server-authoritative and present only on {@link Task}; absent elsewhere. */
export interface SyncableRecord {
  id: string;
  updated_at: string;
  deleted_at: string | null;
  rev?: number;
}

export type EntityKind =
  | "task"
  | "rule"
  | "override"
  | "label"
  | "topic"
  | "calendar"
  | "theme"
  | "preferences";

/**
 * The full record graph exchanged during sync — identical entity set to the
 * backup envelope, but without the envelope's version/preferences wrapper. Every
 * array carries WHOLE rows incl. soft-deleted tombstones (deleted_at != null), so
 * deletes propagate between devices.
 */
export interface SyncGraph {
  tasks: Task[];
  recurrenceRules: RecurrenceRule[];
  recurrenceOverrides: RecurrenceOverride[];
  labels: Label[];
  topics: Topic[];
  calendars: Calendar[];
  themes: ThemeConfig[];
  preferences: AppPreferencesRecord[];
}

/** Set-valued (union-merged) fields per entity. Everything not listed here is a
 *  scalar resolved by last-write-wins. Only Task carries set fields today. */
export const SET_FIELDS: Record<EntityKind, readonly string[]> = {
  task: ["label_ids", "topic_ids", "reminder_offsets"],
  rule: [],
  override: [],
  label: [],
  topic: [],
  calendar: [],
  theme: [],
  preferences: [],
};

/**
 * Fields resolved by their OWN revision instead of the record's LWW order.
 * Today only the Google remote link on a task (see `tasks/remote-link.ts`): it is
 * adopted without bumping `updated_at`, so record-level LWW cannot order it, and
 * `mergeRemoteLink` grafts the winning block onto the LWW winner separately.
 */
export const LINK_FIELDS: Record<EntityKind, readonly string[]> = {
  task: ["external_id", "ical_uid", "etag", "source", "external_calendar_id", "link_rev"],
  rule: [],
  override: [],
  label: [],
  topic: [],
  calendar: [],
  theme: [],
  preferences: [],
};

/** Fields excluded from conflict logging — pure identity/bookkeeping, never a
 *  meaningful "lost edit". */
export const BOOKKEEPING_FIELDS: readonly string[] = [
  "id",
  "created_at",
  "updated_at",
  "rev",
];

/** A scalar value from the losing side of a merge, preserved so the user's edit
 *  is not silently discarded (the "keep the loser in a tombstone" rule). */
export interface FieldConflict {
  kind: EntityKind;
  id: string;
  field: string;
  kept: unknown;
  discarded: unknown;
}

/** Outcome of merging a single record pair. */
export interface RecordMerge<T> {
  merged: T;
  conflicts: FieldConflict[];
  /** merged differs from the LOCAL input → caller must write it to local store. */
  changedLocally: boolean;
  /** merged differs from the REMOTE input → caller must push it to the server. */
  changedRemotely: boolean;
}

/** Outcome of merging two whole graphs. */
export interface GraphMerge {
  /** The converged graph (deterministic regardless of local/remote argument order). */
  merged: SyncGraph;
  /** Every scalar value shadowed by a conflicting concurrent edit. */
  conflicts: FieldConflict[];
  /** Rows to write into the LOCAL store (referential integrity already applied). */
  toApplyLocally: SyncGraph;
  /** Rows the server is missing or has an older copy of — to push. */
  toPushRemotely: SyncGraph;
}

export function emptyGraph(): SyncGraph {
  return {
    tasks: [],
    recurrenceRules: [],
    recurrenceOverrides: [],
    labels: [],
    topics: [],
    calendars: [],
    themes: [],
    preferences: [],
  };
}
